import React from 'react';
import { Box, keyframes } from '@mui/material';

export interface RunwayBackdropProps {
  isActive: boolean;
}

/**
 * CSS/SVG-based runway stage background with animated lighting effects.
 *
 * Renders a dark gradient base with animated radial gradients simulating
 * stage spotlights, side panels representing audience seating areas, and
 * a runway floor perspective. Uses viewport units for responsive scaling.
 * Only renders when isActive is true.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6
 */

// Spotlight pulse animations — subtle so they don't overpower the outfit
const spotlightPulse1 = keyframes`
  0%, 100% { opacity: 0.18; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.08); }
`;

const spotlightPulse2 = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(1.05); }
  50% { opacity: 0.25; transform: scale(0.95); }
`;

const spotlightPulse3 = keyframes`
  0%, 100% { opacity: 0.12; transform: scale(0.97); }
  50% { opacity: 0.22; transform: scale(1.06); }
`;

// Slow sweeping spotlight that moves across the stage
const spotlightSweep = keyframes`
  0% { transform: translateX(-15vw) scale(1); opacity: 0.08; }
  25% { transform: translateX(0vw) scale(1.1); opacity: 0.15; }
  50% { transform: translateX(15vw) scale(1); opacity: 0.08; }
  75% { transform: translateX(0vw) scale(1.1); opacity: 0.15; }
  100% { transform: translateX(-15vw) scale(1); opacity: 0.08; }
`;

// Audience flash effect (camera flashes)
const cameraFlash = keyframes`
  0%, 92%, 100% { opacity: 0; }
  93% { opacity: 0.6; }
  94% { opacity: 0; }
  96% { opacity: 0.4; }
  97% { opacity: 0; }
`;

const RunwayBackdrop: React.FC<RunwayBackdropProps> = ({ isActive }) => {
  if (!isActive) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: 'hidden',
        // Runway background image — slightly faded so outfits pop
        backgroundImage: 'url(/assets/runwayImage.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      aria-hidden="true"
    >
      {/* Lightening overlay — makes the backdrop subtler so outfits are the focus */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          pointerEvents: 'none',
        }}
      />

      {/* ── Camera flash effects overlaid on the image ── */}
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '12%',
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 0 8px 4px rgba(255, 255, 255, 0.8)',
          animation: `${cameraFlash} 7s ease-in-out infinite`,
          animationDelay: '1s',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '55%',
          left: '8%',
          width: '3px',
          height: '3px',
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 0 6px 3px rgba(255, 255, 255, 0.7)',
          animation: `${cameraFlash} 9s ease-in-out infinite`,
          animationDelay: '4s',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          right: '10%',
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 0 8px 4px rgba(255, 255, 255, 0.8)',
          animation: `${cameraFlash} 8s ease-in-out infinite`,
          animationDelay: '2.5s',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '65%',
          right: '15%',
          width: '3px',
          height: '3px',
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 0 6px 3px rgba(255, 255, 255, 0.7)',
          animation: `${cameraFlash} 11s ease-in-out infinite`,
          animationDelay: '6s',
          pointerEvents: 'none',
        }}
      />

      {/* ── Stage spotlights ── */}

      {/* Main center spotlight — warm white, top-down */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '50vw',
          height: '70vh',
          background: `
            radial-gradient(ellipse at 50% 0%,
              rgba(255, 248, 240, 0.12) 0%,
              rgba(255, 248, 240, 0.04) 40%,
              transparent 70%
            )
          `,
          animation: `${spotlightPulse1} 6s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      {/* Left spotlight — cool blue tint */}
      <Box
        sx={{
          position: 'absolute',
          top: '-5vh',
          left: '10vw',
          width: '35vw',
          height: '60vh',
          background: `
            radial-gradient(ellipse at 30% 0%,
              rgba(140, 160, 255, 0.1) 0%,
              rgba(140, 160, 255, 0.03) 45%,
              transparent 70%
            )
          `,
          animation: `${spotlightPulse2} 8s ease-in-out infinite`,
          animationDelay: '1s',
          pointerEvents: 'none',
        }}
      />

      {/* Right spotlight — warm pink tint */}
      <Box
        sx={{
          position: 'absolute',
          top: '-5vh',
          right: '10vw',
          width: '35vw',
          height: '60vh',
          background: `
            radial-gradient(ellipse at 70% 0%,
              rgba(255, 160, 200, 0.1) 0%,
              rgba(255, 160, 200, 0.03) 45%,
              transparent 70%
            )
          `,
          animation: `${spotlightPulse3} 7s ease-in-out infinite`,
          animationDelay: '2s',
          pointerEvents: 'none',
        }}
      />

      {/* Sweeping spotlight — moves across the stage */}
      <Box
        sx={{
          position: 'absolute',
          top: '-8vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '30vw',
          height: '65vh',
          background: `
            radial-gradient(ellipse at 50% 0%,
              rgba(220, 200, 255, 0.08) 0%,
              rgba(220, 200, 255, 0.02) 50%,
              transparent 70%
            )
          `,
          animation: `${spotlightSweep} 12s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      {/* ── Top edge glow — simulates overhead stage lighting rig ── */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `
            linear-gradient(90deg,
              transparent 5%,
              rgba(180, 160, 255, 0.3) 20%,
              rgba(255, 220, 180, 0.4) 50%,
              rgba(180, 160, 255, 0.3) 80%,
              transparent 95%
            )
          `,
        }}
      />

      {/* ── Bottom runway edge glow ── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: '20%',
          right: '20%',
          height: '2px',
          background: `
            linear-gradient(90deg,
              transparent 0%,
              rgba(200, 180, 255, 0.15) 30%,
              rgba(200, 180, 255, 0.2) 50%,
              rgba(200, 180, 255, 0.15) 70%,
              transparent 100%
            )
          `,
        }}
      />

      {/* ── Vignette overlay — subtle edge darkening to focus attention on center ── */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(ellipse at 50% 45%,
              transparent 40%,
              rgba(0, 0, 0, 0.15) 70%,
              rgba(0, 0, 0, 0.35) 100%
            )
          `,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default RunwayBackdrop;
